import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconDownload } from "@tabler/icons-react";
import { CommonUtils, ApiUtils, HumpUtils } from "utils";
import { LangConstant, ApiConstant, SystemConstant } from "const";
import { Pagination } from "components";
import { ACTIVE_FUNCTION_DISPLAY } from "pages/UserManagement/components/UserActiveFunction";
import { useUserList, useTotalUserList } from "hooks";

const UserListDialog = ({
  open,
  unitId,
  departmentId,
  onClose,
  title,
  sinceTime,
  lastTime,
  joinStatus,
  branchStatus,  // lọc theo branch_status cho dialog thống kê (trạng thái "Chức năng hoạt động")
  filterState,   // lọc theo state cho dialog thống kê (kết hợp với branchStatus)
  includeUnit,
  includeDepartment,
  isOnlineFilter = false,
}) => {
  const { t: getLabel } = useTranslation();

  const [onlineUsers, setOnlineUsers] = useState([]);
  const [onlinePage, setOnlinePage] = useState(1);
  const [isOnlineLoading, setIsOnlineLoading] = useState(false);
  const prevOpenRef = React.useRef(false);

  const { totalUsers } = useTotalUserList(open && isOnlineFilter);

  const { currentPageUsers, pagination, isLoading, handleChangePage, filterUsers, funcExportExcel } = useUserList({
    joinStatus: isOnlineFilter ? 1 : joinStatus,
    branchStatus: isOnlineFilter ? undefined : branchStatus,
    filterState: isOnlineFilter ? undefined : filterState,
    unitId: unitId,
    departmentId: departmentId,
    sinceTime: sinceTime,
    lastTime: lastTime,
    includeUnit: includeUnit !== null && includeUnit !== undefined ? includeUnit : true,
    includeDepartment: includeDepartment !== null && includeDepartment !== undefined ? includeDepartment : true,
  });

  const dialogTitle = React.useMemo(() => {
    if (isOnlineFilter) {
      return "Danh sách người dùng đang trực tuyến (Real-time)";
    }
    // Dialog từ biểu đồ thống kê — dùng title trực tiếp
    if (branchStatus !== undefined || filterState !== undefined) {
      return title || getLabel(LangConstant.TXT_STATISTIC_JOINED_USER_LIST);
    }
    if (joinStatus === 1) {
      if (Boolean(unitId) || Boolean(departmentId)) {
        return getLabel(LangConstant.TXT_STATISTIC_USER_LIST_IN, { title });
      }
      return getLabel(LangConstant.TXT_STATISTIC_JOINED_USER_LIST);
    }
    if (joinStatus === 0) {
      return "Danh sách người dùng Vô hiệu hóa";
    }
    return getLabel(LangConstant.TXT_STATISTIC_NOT_JOINED_USER_LIST);
  }, [isOnlineFilter, branchStatus, filterState, joinStatus, unitId, departmentId, title, getLabel]);

  const countTitle = React.useMemo(() => {
    if (isOnlineFilter) {
      return "Tổng số người dùng đang trực tuyến:";
    }
    if (branchStatus !== undefined || filterState !== undefined) {
      return title ? `${title}:` : getLabel(LangConstant.TXT_STATISTIC_JOINED_USER_COUNT, { title: "" });
    }
    if (joinStatus === 1) {
      if (Boolean(unitId) || Boolean(departmentId)) {
        return getLabel(LangConstant.TXT_STATISTIC_USER_COUNT_TITLE_IN, { title });
      }
      return getLabel(LangConstant.TXT_STATISTIC_JOINED_USER_COUNT, { title });
    }
    if (joinStatus === 0) {
      return "Tổng số người dùng Vô hiệu hóa:";
    }
    return getLabel(LangConstant.TXT_STATISTIC_NOT_JOINED_USER_COUNT, { title });
  }, [isOnlineFilter, branchStatus, filterState, joinStatus, unitId, departmentId, title, getLabel]);

  const onExport = async () => {
    const response = await funcExportExcel();
    if (response) {
      const link = document.createElement("a");
      link.href = response;
      link.download = "Danh_sach_nguoi_su_dung.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  useEffect(() => {
    if (open && !prevOpenRef.current) {
      if (isOnlineFilter) {
        setOnlinePage(1);
      } else {
        handleChangePage(1);
      }
    }
    prevOpenRef.current = open;
  }, [open, isOnlineFilter]);

  useEffect(() => {
    if (open && isOnlineFilter) {
      setOnlinePage(1);
      setIsOnlineLoading(true);
      const fetchOnlineUsers = async () => {
        try {
          const response = await ApiUtils.createApiWithToken(false).get(
            ApiConstant.DEVICES_FILTER,
            HumpUtils.decamelizeKeys({
              state: 1,
              blocked: 0,
              paging: 0,
              limit: 1000,
              orderBy: "last_seen",
              asc: 0,
            }),
          );
          if (response.data?.status === ApiConstant.STT_OK || response.status === ApiConstant.STT_OK) {
            const deviceList = response.data?.data?.data || response.data?.data || [];
            const now = Date.now();

            // Dựng sẵn index accountId -> user một lần (O(n)) để tra cứu O(1) thay vì find O(n) mỗi device
            const usersById = new Map();
            (totalUsers || []).forEach(u => {
              usersById.set(String(u.accountId), u);
            });

            const list = [];
            deviceList.forEach(device => {
              const devices = device.devices || [];
              const maxLastSeen = Math.max(0, ...devices.map(d => Number(d.last_seen || d.lastSeen || 0)));
              if (!(maxLastSeen > 0 && now - maxLastSeen <= 5 * 60 * 1000)) return;

              const accId = String(device.account_id || device.accountId);
              const matched = usersById.get(accId);
              // Không match thì bỏ qua, không push vào list
              if (!matched) return;

              list.push({
                id: accId,
                accountId: accId,
                name: matched.name || device.account_name || device.accountName || "Người dùng",
                mail: matched.mail || "-",
                phone: matched.phone || device.phone || "-",
                unitName: matched.unitName || "-",
                departmentName: matched.departmentName || "-",
                image: matched.image || null,
                branchAccountStatus: matched.branchAccountStatus ?? 1,
                branchAccountState: matched.branchAccountState ?? 1,
                state: matched.state ?? 1,
                lastSeen: maxLastSeen,
              });
            });
            setOnlineUsers(list);
          }
        } catch (e) {
          console.error("Error fetching online users in dialog:", e);
        } finally {
          setIsOnlineLoading(false);
        }
      };
      fetchOnlineUsers();
    }
  }, [open, isOnlineFilter, totalUsers]);

  if (!open) return null;

  const totalPages = isOnlineFilter
    ? (Math.ceil(onlineUsers.length / 10) || 1)
    : pagination.total;

  const currentPage = isOnlineFilter ? onlinePage : pagination.current;

  const displayUsers = isOnlineFilter
    ? onlineUsers.slice((onlinePage - 1) * 10, onlinePage * 10)
    : currentPageUsers;

  const totalCountToDisplay = isOnlineFilter
    ? onlineUsers.length
    : (pagination.serverTotal ?? pagination.total_users ?? filterUsers.length);

  const handlePageChange = (eventOrPage, pageNumber) => {
    const targetPage = pageNumber !== undefined ? pageNumber : eventOrPage;
    const finalPage = parseInt(targetPage) || 1;
    if (isOnlineFilter) {
      setOnlinePage(finalPage);
    } else {
      handleChangePage(finalPage);
    }
  };

  const isTableLoading = isOnlineFilter ? isOnlineLoading : isLoading;

  return (
    <>
      <div className="modal-backdrop fade show"></div>
      <div className="modal modal-blur fade show d-block" tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable" role="document">
          <div className="modal-content">
            <div className="modal-header py-2 d-flex align-items-center">
              <h5 className="modal-title fw-bold flex-grow-1 text-center">{dialogTitle}</h5>
              <button className="btn-close" onClick={onClose}></button>
            </div>
            <div className="modal-body" style={{ padding: "16px" }}>
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div className="d-flex gap-1">
                  <strong>{countTitle}</strong>
                  <span>{totalCountToDisplay} {getLabel(LangConstant.TXT_STATISTIC_PEOPLE_UNIT)}</span>
                </div>
                <button className="btn btn-primary d-flex align-items-center gap-1" onClick={onExport}>
                  <IconDownload size={16} />
                  {getLabel(LangConstant.TXT_STATISTIC_EXPORT)}
                </button>
              </div>

              <div style={{ marginTop: "8px" }}>
                <table className="table table-vcenter">
                  <thead>
                    <tr>
                      <th className="text-center" style={{ width: "5%" }}><strong>{getLabel(LangConstant.TXT_STATISTIC_COL_STT)}</strong></th>
                      <th className="text-center" style={{ width: "25%" }}><strong>{getLabel(LangConstant.TXT_USER)}</strong></th>
                      <th className="text-center" style={{ width: "13%" }}><strong>{getLabel(LangConstant.TXT_UNIT)}</strong></th>
                      <th style={{ width: "13%" }}><strong>{getLabel(LangConstant.TXT_DEPARTMENT)}</strong></th>
                      <th className="text-center" style={{ width: "13%" }}><strong>{getLabel(LangConstant.L_ACTIVE_FUNCTION)}</strong></th>
                    </tr>
                  </thead>
                  {isTableLoading ? (
                    <tbody>
                      <tr>
                        <td colSpan={5} className="text-center">
                          <small>{getLabel(LangConstant.TXT_LOADING)}</small>
                        </td>
                      </tr>
                    </tbody>
                  ) : (
                    <tbody>
                      {displayUsers.length > 0 ? (
                        displayUsers.map((user, index) => (
                          <tr key={index}>
                            <td className="text-center">{(currentPage - 1) * 10 + index + 1}</td>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                {user.image ? (
                                  <span
                                    className="avatar avatar-rounded"
                                    style={{ backgroundImage: `url(${user.image})`, width: 32, height: 32, minWidth: 32 }}
                                  ></span>
                                ) : (
                                  <span
                                    className="avatar avatar-rounded"
                                    style={{
                                      width: 32,
                                      height: 32,
                                      minWidth: 32,
                                      backgroundColor: CommonUtils.stringToColor(user.name),
                                    }}
                                  >
                                    {user.name?.charAt(0)?.toUpperCase()}
                                  </span>
                                )}
                                <span style={{ fontSize: "14px" }}>{user.name} ({user.phone})</span>
                              </div>
                            </td>
                            <td className="text-center">{user.unitName || getLabel(LangConstant.TXT_NOT_FOUND)}</td>
                            <td>{user.departmentName || getLabel(LangConstant.TXT_NOT_FOUND)}</td>
                            <td className="text-center">
                              {(() => {
                                const fn = SystemConstant.resolveActiveFunction(user);
                                if (!fn) return <span />;
                                const { label, badgeClass } = ACTIVE_FUNCTION_DISPLAY[fn];
                                return (
                                  <span className={badgeClass} style={{ cursor: "default", width: "130px", display: "inline-block", textAlign: "center" }}>
                                    {getLabel(label)}
                                  </span>
                                );
                              })()}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={5} className="text-center">
                            <small>{getLabel(LangConstant.TXT_NOT_FOUND)}</small>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  )}
                </table>
              </div>

              {totalPages > 1 && (
                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UserListDialog;
